import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import Login from "./components/Login";
import SolutionProjectList from "./components/SolutionProjectList";
import SolutionTaskViewer from "./components/SolutionTaskViewer";
import "./App.css";

type TabType = "projects";

// Route wrapper component to extract projectId from URL params
const SolutionTaskViewerRoute: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { projectId } = useParams<{ projectId: string }>();
  
  if (!projectId) {
    return <div>Error: Project ID not found</div>;
  }
  
  return <SolutionTaskViewer projectId={projectId} onBack={onBack} />;
};

function App() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>("projects");

  useEffect(() => {
    // Check if user is already logged in
    const storedUserId = localStorage.getItem("userId");
    const storedUsername = localStorage.getItem("username");
    const storedAccessCode = localStorage.getItem("accessCode");
    const storedPermissions = localStorage.getItem("userPermissions");

    if (storedUserId && storedUsername && storedAccessCode && storedPermissions) {
      setUserId(storedUserId);
      setUsername(storedUsername);
      setUserPermissions(JSON.parse(storedPermissions));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = (newUserId: string, newUsername: string, permissions: string[] = []) => {
    setUserId(newUserId);
    setUsername(newUsername);
    setUserPermissions(permissions);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
    localStorage.removeItem("accessCode");
    localStorage.removeItem("userPermissions");
    setUserId(null);
    setUsername(null);
    setUserPermissions([]);
    setIsAuthenticated(false);
    navigate("/");
  };

  const handleProjectSelect = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const handleBackToProjects = () => {
    navigate("/");
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1 onClick={handleBackToProjects} style={{ cursor: "pointer" }}>
          Solution Platform
        </h1>
        <div className="user-info">
          <span>Logged in as: {username}</span>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>
      <main className="App-main">
        <Routes>
          <Route 
            path="/" 
            element={
              <SolutionProjectList 
                onSelectProject={handleProjectSelect}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                userPermissions={userPermissions}
                loading={loading}
                userId={userId}
              />
            } 
          />
          <Route 
            path="/project/:projectId" 
            element={<SolutionTaskViewerRoute onBack={handleBackToProjects} />} 
          />
        </Routes>
      </main>
    </div>
  );
}

export default App;